import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { FaSpinner, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './CataloguePage.module.css';
import ResultsSection from '../../components/ListingScore/components/ResultsSection';
import ActionPlanSection from '../../components/ListingScore/components/ActionPlanSection';
import ComponentAnalysisSection from '../../components/ListingScore/components/ComponentAnalysisSection';
import listingScoreStyles from '../../components/ListingScore/ListingScore.module.css';
import AuthContext from '../../context/AuthContext';
import { navigateToSingleListingUpgrade } from '../../utils/upgradeListingNavigation';

// Constants
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const AUTH_TOKEN_KEY = 'authToken';
const PRODUCTS_PER_PAGE = 100;

// Helper function to convert percentage to points (0-100 -> 0-10)
const convertToPoints = (percentage) => {
    if (percentage === null || percentage === undefined) return 0;
    return (percentage / 10).toFixed(1);
};

// API Service
const fetchProducts = async (token) => {
    const response = await fetch(`${API_BASE_URL}/oauth_amazon/amazon/catalogue`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
    }

    return response.json();
};

// Components
const LoadingState = () => (
    <div className={styles.loadingContainer}>
        <FaSpinner className="fa-spin" />
        <p>Loading your Amazon products...</p>
    </div>
);

const ErrorState = ({ error, onRetry }) => (
    <div className={styles.container}>
        <h1 className={styles.title}>Catalogue</h1>
        <div className={styles.errorMessage}>
            <strong>Error:</strong> {error}
            <button onClick={onRetry} className={styles.retryButton}>
                Try Again
            </button>
        </div>
    </div>
);

const EmptyState = () => (
    <div className={styles.emptyState}>
        <h3>No products found</h3>
        <p>Connect your Amazon account to see your products here.</p>
    </div>
);

const NotConnectedState = ({ onConnect }) => (
    <div className={styles.emptyState}>
        <h3>Amazon Account Not Connected</h3>
        <p>Please connect your Amazon seller account to view your product catalogue.</p>
        <button onClick={onConnect} className={styles.connectButton}>
            Connect Amazon Account
        </button>
    </div>
);

const ProductRow = ({ product, index, onUpgrade, listingScore, onViewScore }) => {
    const hasScore = listingScore && listingScore.has_score;
    const score = listingScore?.score || 0;
    
    const getScoreColor = (score) => {
        if (score >= 80) return '#4CAF50'; // Green
        if (score >= 60) return '#FFA726'; // Orange
        return '#F44336'; // Red
    };

    return (
        <tr className={styles.tableRow}>
            <td className={styles.serialNumberCell}>{index + 1}</td>
            <td className={styles.asinCell}>{product.asin || 'N/A'}</td>
            <td className={styles.skuCell}>{product.sku || 'N/A'}</td>
            <td className={styles.titleCell}>{product.title || 'No title available'}</td>
            <td className={styles.listingScoreCell}>
                {hasScore ? (
                    <div className={styles.scoreContainer}>
                        <span 
                            className={styles.scoreValue}
                            style={{ color: getScoreColor(score) }}
                        >
                            {convertToPoints(score)}/10
                        </span>
                        <button
                            onClick={() => onViewScore(product.asin, listingScore)}
                            className={styles.viewResultLink}
                        >
                            View Result
                        </button>
                    </div>
                ) : (
                    <span className={styles.noScore}>—</span>
                )}
            </td>
            <td className={styles.actionCell}>
                <button
                    onClick={() => onUpgrade(product)}
                    className={`${styles.btn} ${styles.btnPrimary} ${styles.upgradeButton}`}
                    aria-label={`Upgrade listing for ${product.sku || 'product'}`}
                >
                    Upgrade Listing
                </button>
            </td>
        </tr>
    );
};

const Pagination = React.memo(({ currentPage, totalPages, onPageChange }) => {
    const handlePrevPage = useCallback(() => onPageChange(currentPage - 1), [currentPage, onPageChange]);
    const handleNextPage = useCallback(() => onPageChange(currentPage + 1), [currentPage, onPageChange]);
    
    if (totalPages <= 1) return null;

    return (
        <div className={styles.pagination}>
            <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={styles.paginationButton}
                aria-label="Previous page"
            >
                <FaChevronLeft />
            </button>
            
            <span className={styles.pageInfo}>
                Page {currentPage} of {totalPages}
            </span>
            
            <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={styles.paginationButton}
                aria-label="Next page"
            >
                <FaChevronRight />
            </button>
        </div>
    );
});

const ProductsTable = React.memo(({ products, onUpgrade, currentPage, productsPerPage, listingScores, onViewScore }) => {
    const currentProducts = useMemo(() => {
        const startIndex = (currentPage - 1) * productsPerPage;
        const endIndex = startIndex + productsPerPage;
        return products.slice(startIndex, endIndex);
    }, [products, currentPage, productsPerPage]);

    const startIndex = useMemo(() => (currentPage - 1) * productsPerPage, [currentPage, productsPerPage]);

    return (
        <div className={styles.tableContainer}>
            <table className={styles.table}>
                <thead>
                    <tr className={styles.tableHeader}>
                        <th className={styles.serialNumberColumn}>S.No</th>
                        <th className={styles.asinColumn}>ASIN</th>
                        <th className={styles.skuColumn}>SKU</th>
                        <th className={styles.titleColumn}>Title</th>
                        <th className={styles.listingScoreColumn}>Listing Score</th>
                        <th className={styles.actionColumn}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {currentProducts.map((product, index) => (
                        <ProductRow
                            key={`${product.asin}-${product.sku}-${startIndex + index}`}
                            product={product}
                            index={startIndex + index}
                            onUpgrade={onUpgrade}
                            listingScore={listingScores[product.asin]}
                            onViewScore={onViewScore}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
});

// Main Component
const CataloguePage = () => {
    const authCtx = useContext(AuthContext);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [listingScores, setListingScores] = useState({});
    const [loadingScores, setLoadingScores] = useState(false);
    const [selectedScoreData, setSelectedScoreData] = useState(null);
    const [showDetailedView, setShowDetailedView] = useState(false);
    const [selectedTab, setSelectedTab] = useState('actionPlan');
    const navigate = useNavigate();
    const location = useLocation();

    // Check if Amazon is connected
    const isAmazonConnected = authCtx.user?.amazon_status === true;

    const fetchAmazonProducts = useCallback(async () => {
        // Only fetch if Amazon is connected
        if (!isAmazonConnected) {
            setLoading(false);
            setProducts([]);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setCurrentPage(1);
            
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            if (!token) {
                throw new Error('No authentication token found. Please log in again.');
            }
            
            const data = await fetchProducts(token);
            setProducts(data.products || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [isAmazonConnected]);

    const fetchListingScores = useCallback(async () => {
        if (products.length === 0) return;

        try {
            setLoadingScores(true);
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            if (!token) return;

            const asins = products.map(p => p.asin).filter(Boolean);
            if (asins.length === 0) return;

            const response = await fetch(`${API_BASE_URL}/listing_score/asin-scores-bulk`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ asins }),
            });

            if (response.ok) {
                const scoresData = await response.json();
                setListingScores(scoresData);
            }
        } catch (err) {
            console.error('Error fetching listing scores:', err);
        } finally {
            setLoadingScores(false);
        }
    }, [products]);

    const handleUpgradeListing = useCallback((product) => {
        // If we only have a catalogue row, fall back to the ASIN lookup flow.
        // If the user is upgrading from a detailed score view, we will pass the full payload via ResultsSection.
        navigate('/dashboard/generate/single', {
            state: {
                isNewListing: false,
                projectName: product.sku || 'Unknown SKU',
                initialAsin: product.asin || ''
            }
        });
    }, [navigate]);

    const handleRetry = useCallback(() => {
        fetchAmazonProducts();
    }, [fetchAmazonProducts]);

    const handleConnectAmazon = useCallback(() => {
        // Navigate to marketplaces page to connect Amazon
        navigate('/dashboard/market-places');
    }, [navigate]);

    const handlePageChange = useCallback((newPage) => {
        setCurrentPage(Math.max(1, Math.min(newPage, Math.ceil(products.length / PRODUCTS_PER_PAGE))));
    }, [products.length]);

    // Memoized values
    const hasProducts = useMemo(() => products.length > 0, [products.length]);
    const totalPages = useMemo(() => Math.ceil(products.length / PRODUCTS_PER_PAGE), [products.length]);

    useEffect(() => {
        fetchAmazonProducts();
    }, [fetchAmazonProducts, isAmazonConnected]);

    // Check for scores passed from redirect (from bulk listing scorer)
    useEffect(() => {
        if (location.state?.listingScores) {
            // Use scores from redirect state (from API response)
            setListingScores(location.state.listingScores);
            // Clear the state to avoid reusing on refresh
            window.history.replaceState({}, document.title);
        } else if (products.length > 0) {
            // Only fetch from DB if no scores were passed from redirect
            fetchListingScores();
        }
    }, [location.state, products, fetchListingScores]);

    const handleViewScore = useCallback((asin, scoreData) => {
        if (!scoreData || !scoreData.has_score) return;

        // Transform the score data to match the format expected by components
        const analysis = scoreData.analysis || {};
        const productInfo = scoreData.product_info || {};

        const transformedData = {
            asin: asin,
            title: productInfo.title || 'Product Title',
            main_image: productInfo.main_image || '',
            b_plus_images: productInfo.b_plus_images || productInfo.all_images || [],
            description: productInfo.description || '',
            bullet_points: productInfo.bullet_points || [],
            specifications: productInfo.specifications || [],
            a_plus_content: productInfo.a_plus_content || false,
            analysis: {
                total_score: scoreData.total_score || 0,
                percentage_score: scoreData.score || 0,
                overall_analysis: analysis.overall_analysis || '',
                component_scores: analysis.component_scores || {},
                competitive_advantages: analysis.competitive_advantages || [],
                improvement_areas: analysis.improvement_areas || [],
                critical_gaps: analysis.critical_gaps || [],
                recommendations: analysis.recommendations || [],
            },
            product_info: productInfo,
        };

        setSelectedScoreData(transformedData);
        setShowDetailedView(true);
    }, []);

    const handleCloseDetailedView = useCallback(() => {
        setShowDetailedView(false);
        setSelectedScoreData(null);
        setSelectedTab('actionPlan');
    }, []);

    // Render states
    if (loading) return <LoadingState />;
    
    // Check if Amazon is connected first
    if (!isAmazonConnected) {
        return (
            <div className={styles.container}>
                <h1 className={styles.title}>Catalogue</h1>
                <p className={styles.subtitle}>Manage your Amazon product listings</p>
                <NotConnectedState onConnect={handleConnectAmazon} />
            </div>
        );
    }

    if (error) return <ErrorState error={error} onRetry={handleRetry} />;

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Catalogue</h1>
            <p className={styles.subtitle}>Manage your Amazon product listings</p>

            {hasProducts ? (
                <>
                    <ProductsTable 
                        products={products} 
                        onUpgrade={handleUpgradeListing}
                        currentPage={currentPage}
                        productsPerPage={PRODUCTS_PER_PAGE}
                        listingScores={listingScores}
                        onViewScore={handleViewScore}
                    />
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </>
            ) : (
                <EmptyState />
            )}

            {/* Detailed View (Direct View) */}
            {showDetailedView && selectedScoreData && (
                <div className={styles.modalOverlay}>
                    <div className={styles.detailedViewModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.detailedViewHeader}>
                            <h2>Detailed Listing Score Analysis</h2>
                            <button 
                                className={styles.closeButton}
                                onClick={handleCloseDetailedView}
                                aria-label="Close"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className={styles.detailedViewContent}>
                            {selectedScoreData.analysis && (
                                <>
                                    <ResultsSection
                                        productData={selectedScoreData}
                                        onUpgradeListing={(pd) => navigateToSingleListingUpgrade(navigate, pd)}
                                    />
                                    
                                    <section className={listingScoreStyles.section2}>
                                        <div className={listingScoreStyles.toggleContainer}>
                                            <button
                                                className={selectedTab === 'actionPlan' 
                                                    ? `${listingScoreStyles.toggleButton} ${listingScoreStyles.activeToggle}` 
                                                    : listingScoreStyles.toggleButton}
                                                onClick={() => setSelectedTab('actionPlan')}
                                            >
                                                Action Plans
                                            </button>
                                            <button
                                                className={selectedTab === 'componentAnalysis' 
                                                    ? `${listingScoreStyles.toggleButton} ${listingScoreStyles.activeToggle}` 
                                                    : listingScoreStyles.toggleButton}
                                                onClick={() => setSelectedTab('componentAnalysis')}
                                            >
                                                Component Analysis
                                            </button>
                                        </div>
                                        {selectedTab === 'actionPlan' && (
                                            <ActionPlanSection 
                                                productData={{ analysis: selectedScoreData.analysis }} 
                                                showDetailedAnalysis={true}
                                            />
                                        )}
                                        {selectedTab === 'componentAnalysis' && (
                                            <ComponentAnalysisSection productData={{ analysis: selectedScoreData.analysis }} />
                                        )}
                                    </section>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CataloguePage;
